import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import "./CMS.css";

import {
  FaFileAlt,
  FaImage,
  FaUser,
  FaHistory,
  FaUpload,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import AdminRoles from "./AdminRoles";
import AuditLogs from "./AuditLogs";
import { apiGet, apiPost } from "../../Services/Services";
import { ENDPOINTS } from "../../Services/Endpoints";
import {
  CMS_SECTION_SLUGS,
  buildCmsFormData,
} from "./cmsFormData";

function UploadBox({ accept = "image/*", onFile, className = "upload-box", children, previewUrl, fileName }) {
  const inputRef = useRef(null);

  return (
    <div
      className={className}
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
    >
      {children}
      {previewUrl && (
        <div className="upload-preview">
          <img src={previewUrl} alt="Preview" className="upload-preview-image" />
          {fileName && <p className="upload-file-name">{fileName}</p>}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}

const PlatformCMS = () => {
  const [activeTab, setActiveTab] = useState("landing");
  const [saving, setSaving] = useState(false);

  // DROPDOWN STATE
  const [landingType, setLandingType] = useState("");
  const [sectionError, setSectionError] = useState("");
  const [heroForm, setHeroForm] = useState({
    heading: "",
    subHeading: "",
    button1Text: "",
    button2Text: "",
  });
  const [heroFiles, setHeroFiles] = useState({
    backgroundImage: null,
    characterImage: null,
  });
  const [heroPreviews, setHeroPreviews] = useState({
    backgroundImage: null,
    characterImage: null,
  });
  const [howItWorksFiles, setHowItWorksFiles] = useState({});
  const [howItWorksPreviews, setHowItWorksPreviews] = useState({});
  const [featuredFiles, setFeaturedFiles] = useState({});
  const [featuredPreviews, setFeaturedPreviews] = useState({});
  const [discoverFiles, setDiscoverFiles] = useState({
    backgroundImage: null,
    video: null,
  });
  const [discoverPreviews, setDiscoverPreviews] = useState({
    backgroundImage: null,
    video: null,
  });
  const [attractionFiles, setAttractionFiles] = useState({});
  const [attractionPreviews, setAttractionPreviews] = useState({});
  const [ctaFiles, setCtaFiles] = useState({
    topAvatarImage: null,
    mapImage: null,
    bottomImage1: null,
    bottomImage2: null,
    bottomImage3: null,
  });
  const [ctaPreviews, setCtaPreviews] = useState({
    topAvatarImage: null,
    mapImage: null,
    bottomImage1: null,
    bottomImage2: null,
    bottomImage3: null,
  });


  const updateHeroForm = (field, value) => {
    setHeroForm((prev) => ({ ...prev, [field]: value }));
  };

  const setHowItWorksFile = (index, file) => {
    setHowItWorksFiles((prev) => ({ ...prev, [index]: file }));
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setHowItWorksPreviews((prev) => ({ ...prev, [index]: previewUrl }));
    }
  };

  const setFeaturedFile = (index, file) => {
    setFeaturedFiles((prev) => ({ ...prev, [index]: file }));
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setFeaturedPreviews((prev) => ({ ...prev, [index]: previewUrl }));
    }
  };

  const setAttractionFile = (index, file) => {
    setAttractionFiles((prev) => ({ ...prev, [index]: file }));
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setAttractionPreviews((prev) => ({ ...prev, [index]: previewUrl }));
    }
  };

  // ── Hero stats ──
  const [heroStats, setHeroStats] = useState([
    { label: "", value: "", duration: "" },
  ]);

  const updateHeroStat = (index, field, val) => {
    const updated = [...heroStats];
    updated[index][field] = val;
    setHeroStats(updated);
  };

  // ── How It Works cards ──
  const [howItWorksTitle, setHowItWorksTitle] = useState("");
  const [howItWorksSubtitle, setHowItWorksSubtitle] = useState("");
  const [howItWorksCards, setHowItWorksCards] = useState([
    { heading: "", paragraph: "" },
  ]);

  const updateHowCard = (index, field, val) => {
    const updated = [...howItWorksCards];
    updated[index][field] = val;
    setHowItWorksCards(updated);
  };

  const addHowItWorksCard = () => {
    setHowItWorksCards([...howItWorksCards, { heading: "", paragraph: "" }]);
  };

  const removeHowItWorksCard = (index) => {
    setHowItWorksCards(howItWorksCards.filter((_, i) => i !== index));
    setHowItWorksFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[index];
      return newFiles;
    });
    setHowItWorksPreviews((prev) => {
      const newPreviews = { ...prev };
      if (newPreviews[index]) {
        URL.revokeObjectURL(newPreviews[index]);
        delete newPreviews[index];
      }
      return newPreviews;
    });
  };

  // ── Featured Challenges ──
  const [featuredTitle, setFeaturedTitle] = useState("");
  const [featuredSubtitle, setFeaturedSubtitle] = useState("");
  const [featuredChallenges, setFeaturedChallenges] = useState([
    { title: "", category: "", distance: "" },
  ]);

  const updateChallenge = (index, field, val) => {
    const updated = [...featuredChallenges];
    updated[index][field] = val;
    setFeaturedChallenges(updated);
  };

  const addChallenge = () =>
    setFeaturedChallenges([...featuredChallenges, { title: "", category: "", distance: "" }]);

  const removeChallenge = (index) =>
    setFeaturedChallenges(featuredChallenges.filter((_, i) => i !== index));

  // ── Discover Ascender ──
  const [discoverTitle, setDiscoverTitle] = useState("");
  const [discoverStats, setDiscoverStats] = useState([
    { label: "", value: "" },  // For Total KM and Active Users
    { label: "", value: "" },  // For Customer Satisfaction
  ]);

  const updateDiscoverStat = (index, field, val) => {
    const updated = [...discoverStats];
    updated[index][field] = val;
    setDiscoverStats(updated);
  };

  // ── Our Attractions ──
  const [attractionsTitle, setAttractionsTitle] = useState("");
  const [attractionsSubtitle, setAttractionsSubtitle] = useState("");
  const [attractions, setAttractions] = useState([
    { title: "", tag: "" },
  ]);

  const updateAttraction = (index, field, val) => {
    const updated = [...attractions];
    updated[index][field] = val;
    setAttractions(updated);
  };

  const addAttraction = () =>
    setAttractions([...attractions, { title: "", tag: "" }]);

  const removeAttraction = (index) => {
    setAttractions(attractions.filter((_, i) => i !== index));
    setAttractionFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[index];
      return newFiles;
    });
    setAttractionPreviews((prev) => {
      const newPreviews = { ...prev };
      if (newPreviews[index]) {
        URL.revokeObjectURL(newPreviews[index]);
        delete newPreviews[index];
      }
      return newPreviews;
    });
  };

  // ── Start Challenge / CTA Banner ──
  const [ctaBannerHeading, setCtaBannerHeading] = useState("");
  const [ctaBannerSubtext, setCtaBannerSubtext] = useState("");
  const [ctaBannerBtnText, setCtaBannerBtnText] = useState("");

  // Function to fetch and populate CMS data
  const fetchCMSData = async () => {
    try {
      const response = await apiGet(ENDPOINTS.cms.getDetails);
      const data = response?.data;

      if (data) {
        // Populate Hero Section
        if (data.heroSection) {
          const hero = data.heroSection;
          setHeroForm({
            heading: hero.heading || "",
            subHeading: hero.subHeading || "",
            button1Text: hero.button1Text || "",
            button2Text: hero.button2Text || "",
          });

          setHeroStats([{
            label: hero.milestones || "",
            value: hero.distance || "",
            duration: hero.duration || "",
          }]);

          // Set image URLs for preview (assuming you have a base URL for images)
          if (hero.backgroundImage) {
            setHeroPreviews(prev => ({ ...prev, backgroundImage: hero.backgroundImage }));
          }
          if (hero.characterImage) {
            setHeroPreviews(prev => ({ ...prev, characterImage: hero.characterImage }));
          }
        }

        // Populate How It Works Section
        if (data.howItWorks) {
          const howItWorks = data.howItWorks;
          setHowItWorksTitle(howItWorks.sectionHeading || "");
          setHowItWorksSubtitle(howItWorks.sectionDescription || "");

          if (howItWorks.cards && howItWorks.cards.length > 0) {
            const cards = howItWorks.cards.map(card => ({
              heading: card.title || "",
              paragraph: card.description || "",
            }));
            setHowItWorksCards(cards);

            // Set image previews for cards
            const previews = {};
            howItWorks.cards.forEach((card, index) => {
              if (card.image) {
                previews[index] = card.image;
              }
            });
            setHowItWorksPreviews(previews);
          }
        }

        // Populate Discover Section
        if (data.discoverSection) {
          const discover = data.discoverSection;
          setDiscoverTitle(discover.sectionTitle || "");

          setDiscoverStats([
            {
              label: discover.totalKm || "",
              value: discover.activeUsers || ""
            },
            {
              label: "",
              value: discover.customerSatisfaction || ""
            },
          ]);

          if (discover.backgroundImage) {
            setDiscoverPreviews(prev => ({ ...prev, backgroundImage: discover.backgroundImage }));
          }
          if (discover.videoUrl) {
            setDiscoverPreviews(prev => ({ ...prev, video: discover.videoUrl }));
          }
        }

        // Populate Attractions Section
        if (data.attractions) {
          const attractionsData = data.attractions;
          setAttractionsTitle(attractionsData.sectionTitle || "");
          setAttractionsSubtitle(attractionsData.sectionDescription || "");

          if (attractionsData.items && attractionsData.items.length > 0) {
            const items = attractionsData.items.map(item => ({
              title: item.title || "",
              tag: "", // Tag field not present in the response
            }));
            setAttractions(items);

            // Set image previews for attractions
            const previews = {};
            attractionsData.items.forEach((item, index) => {
              if (item.image) {
                previews[index] = item.image;
              }
            });
            setAttractionPreviews(previews);
          }
        }

        // Populate CTA Section
        if (data.ctaSection) {
          const cta = data.ctaSection;
          setCtaBannerHeading(cta.heading || "");
          setCtaBannerSubtext(cta.subHeading || "");
          setCtaBannerBtnText(cta.buttonText || "");

          // Set image previews
          if (cta.topAvatarImage) {
            setCtaPreviews(prev => ({ ...prev, topAvatarImage: cta.topAvatarImage }));
          }
          if (cta.mapImage) {
            setCtaPreviews(prev => ({ ...prev, mapImage: cta.mapImage }));
          }
          if (cta.bottomImage1) {
            setCtaPreviews(prev => ({ ...prev, bottomImage1: cta.bottomImage1 }));
          }
          if (cta.bottomImage2) {
            setCtaPreviews(prev => ({ ...prev, bottomImage2: cta.bottomImage2 }));
          }
          if (cta.bottomImage3) {
            setCtaPreviews(prev => ({ ...prev, bottomImage3: cta.bottomImage3 }));
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch CMS data:", error);
    }
  };

  useEffect(() => {
    fetchCMSData();
  }, []);

  const handleSave = async () => {
    if (!landingType) {
      setSectionError("Please select a landing page section.");
      return;
    }

    const slug = CMS_SECTION_SLUGS[landingType];

    if (!slug) {
      toast.info("API for this section is not available yet.");
      return;
    }

    setSectionError("");

    let formData = null;

    switch (landingType) {
      case "hero":
        formData = buildCmsFormData("hero", {
          ...heroForm,
          heroStats,
          files: heroFiles,
        });
        break;
      case "howitworks":
        formData = buildCmsFormData("howitworks", {
          sectionHeading: howItWorksTitle,
          sectionDescription: howItWorksSubtitle,
          cards: howItWorksCards,
          cardFiles: howItWorksFiles,
        });
        break;
      case "attractions":
        formData = buildCmsFormData("attractions", {
          sectionTitle: attractionsTitle,
          sectionDescription: attractionsSubtitle,
          attractions,
          itemFiles: attractionFiles,
        });
        break;
      case "ctabanner":
        formData = buildCmsFormData("ctabanner", {
          heading: ctaBannerHeading,
          subHeading: ctaBannerSubtext,
          buttonText: ctaBannerBtnText,
          files: ctaFiles,
        });
        break;
      case "discoverascender":
        formData = buildCmsFormData("discoverascender", {
          sectionTitle: discoverTitle,
          discoverStats,
          files: discoverFiles,
        });
        break;
      default:
        break;
    }

    if (!formData) return;

    // Debug logging
    for (let pair of formData.entries()) {
      console.log(pair[0], pair[1]);
    }

    setSaving(true);
    try {
      // await apiPost(ENDPOINTS.cms.create(slug), formData, { timeout: 120000 }, {
      await apiPost(ENDPOINTS.cms.create(slug), formData, {
        showSuccessToast: true,
        useApiSuccessMessage: true,
      });
      // Refetch data after successful save
      await fetchCMSData();
    } catch {
      // Errors handled by api interceptor
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cms-container">
      <h1 className="cms-title">Platform CMS</h1>

      <p className="cms-subtitle">
        Manage website content, admin roles, and monitor system activity
      </p>

      {/* ================= TABS ================= */}

      <div className="cms-tabs">
        <div
          className={`cms-tab ${activeTab === "landing" ? "active" : ""}`}
          onClick={() => setActiveTab("landing")}
        >
          <FaFileAlt />
          <span>Landing Page Content</span>
        </div>
        <div
          className={`cms-tab ${activeTab === "admin" ? "active" : ""}`}
          onClick={() => setActiveTab("admin")}
        >
          <FaUser />
          <span>Admin Roles</span>
        </div>

        <div
          className={`cms-tab ${activeTab === "audit" ? "active" : ""}`}
          onClick={() => setActiveTab("audit")}
        >
          <FaHistory />
          <span>Audit Logs</span>
        </div>
      </div>

      {/* ================= LANDING PAGE ================= */}

      {activeTab === "landing" && (
        <div className="card">
          <h2 className="section-title">Landing Page Content</h2>

          {/* ================= DROPDOWN ================= */}

          <div className="form-group">
            <label>Select Landing Page Section</label>

            <select
              className={`form-control${sectionError ? " cms-section-select--error" : ""}`}
              value={landingType}
              onChange={(e) => {
                setLandingType(e.target.value);
                if (e.target.value) setSectionError("");
              }}
              aria-invalid={Boolean(sectionError)}
            >
              <option value="" disabled>
                Select a section
              </option>
              <option value="hero">Hero Section</option>
              <option value="howitworks">How It Works</option>
              <option value="discoverascender">Discover Ascender</option>
              <option value="attractions">Our Attractions</option>
              <option value="ctabanner">Start Challenge Banner</option>
            </select>
            {sectionError ? (
              <p className="cms-section-error" role="alert">
                {sectionError}
              </p>
            ) : null}
          </div>

          {/* ================= HERO ================= */}

          {landingType === "hero" && (
            <>
              <UploadBox
                accept="image/*"
                onFile={(file) => {
                  setHeroFiles((prev) => ({ ...prev, backgroundImage: file }));
                  if (file) {
                    const previewUrl = URL.createObjectURL(file);
                    setHeroPreviews((prev) => ({ ...prev, backgroundImage: previewUrl }));
                  }
                }}
                previewUrl={heroPreviews.backgroundImage}
                fileName={heroFiles.backgroundImage?.name}
              >
                <FaUpload className="upload-icon" />
                <p>Upload Hero Banner Image</p>
                <span>Recommended size: 1920 × 1080px</span>
              </UploadBox>

              <UploadBox
                accept="image/*"
                onFile={(file) => {
                  setHeroFiles((prev) => ({ ...prev, characterImage: file }));
                  if (file) {
                    const previewUrl = URL.createObjectURL(file);
                    setHeroPreviews((prev) => ({ ...prev, characterImage: previewUrl }));
                  }
                }}
                previewUrl={heroPreviews.characterImage}
                fileName={heroFiles.characterImage?.name}
              >
                <FaUpload className="upload-icon" />
                <p>Upload Character Image</p>
                <span>Optional character illustration</span>
              </UploadBox>

              <div className="form-group">
                <label>Main Headline</label>
                <input
                  type="text"
                  className="form-control"
                  value={heroForm.heading}
                  onChange={(e) => updateHeroForm("heading", e.target.value)}
                  placeholder="Enter main headline"
                />
              </div>

              <div className="form-group">
                <label>Subheadline</label>
                <textarea
                  className="form-control"
                  value={heroForm.subHeading}
                  onChange={(e) => updateHeroForm("subHeading", e.target.value)}
                  placeholder="Enter subheadline"
                ></textarea>
              </div>

              <div className="form-group">
                <label>Primary Button Text</label>
                <input
                  type="text"
                  className="form-control"
                  value={heroForm.button1Text}
                  onChange={(e) => updateHeroForm("button1Text", e.target.value)}
                  placeholder="Enter primary button text"
                />
              </div>

              <div className="form-group">
                <label>Secondary Button Text</label>
                <input
                  type="text"
                  className="form-control"
                  value={heroForm.button2Text}
                  onChange={(e) => updateHeroForm("button2Text", e.target.value)}
                  placeholder="Enter secondary button text (e.g. Learn More)"
                />
              </div>

              {/* Hero Stats */}
              <div className="cms-subsection">
                <h3 className="subsection-title">Hero Stats</h3>
                <p className="subsection-desc">
                  Enter distance, milestones, and duration for the hero section
                </p>

                {heroStats.map((stat, index) => (
                  <div className="cms-repeater-row" key={index}>
                    <div className="form-group flex-1">
                      <label>Distance (KM)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={stat.value}
                        onChange={(e) => updateHeroStat(index, "value", e.target.value)}
                        placeholder="e.g. 1200 KM"
                      />
                    </div>
                    <div className="form-group flex-1">
                      <label>Milestones</label>
                      <input
                        type="text"
                        className="form-control"
                        value={stat.label}
                        onChange={(e) => updateHeroStat(index, "label", e.target.value)}
                        placeholder="e.g. 12M"
                      />
                    </div>
                    <div className="form-group flex-1">
                      <label>Duration/Percentage</label>
                      <input
                        type="text"
                        className="form-control"
                        value={stat.duration}
                        onChange={(e) => updateHeroStat(index, "duration", e.target.value)}
                        placeholder="e.g. 95%"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ================= HOW IT WORKS ================= */}

          {landingType === "howitworks" && (
            <>
              <div className="form-group">
                <label>Section Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={howItWorksTitle}
                  onChange={(e) => {
                    setHowItWorksTitle(e.target.value);
                  }}
                  placeholder="e.g. How It Works"
                />
              </div>

              <div className="form-group">
                <label>Section Subtitle</label>
                <textarea
                  className="form-control"
                  value={howItWorksSubtitle}
                  onChange={(e) => {
                    setHowItWorksSubtitle(e.target.value);
                  }}
                  placeholder="Enter subtitle text"
                ></textarea>
              </div>

              <div className="cms-subsection">
                <h3 className="subsection-title">Step Cards</h3>
                <p className="subsection-desc">
                  Each card shows an image, heading and paragraph. Add up to as many steps as needed.
                </p>

                {howItWorksCards.map((card, index) => (
                  <div className="cms-repeater-card" key={index}>
                    <div className="repeater-header">
                      <span className="repeater-label">Step {index + 1}</span>
                      {howItWorksCards.length > 1 && (
                        <button
                          className="btn-remove"
                          onClick={() => removeHowItWorksCard(index)}
                          title="Remove step"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                    

                    <UploadBox
                      className="upload-box upload-box--sm"
                      accept="image/*"
                      onFile={(file) => setHowItWorksFile(index, file)}
                      previewUrl={howItWorksPreviews[index]}
                      fileName={howItWorksFiles[index]?.name}
                    >
                      <FaUpload className="upload-icon" />
                      <p>Upload Step Image</p>
                    </UploadBox>

                    <div className="form-group">
                      <label>Card Heading</label>
                      <input
                        type="text"
                        className="form-control"
                        value={card.heading}
                        onChange={(e) => updateHowCard(index, "heading", e.target.value)}
                        placeholder="e.g. Physical Challenge"
                      />
                    </div>

                    <div className="form-group">
                      <label>Card Paragraph</label>
                      <textarea
                        className="form-control"
                        value={card.paragraph}
                        onChange={(e) => updateHowCard(index, "paragraph", e.target.value)}
                        placeholder="Enter description"
                      ></textarea>
                    </div>
                  </div>
                ))}

                {/* <button className="btn-add-row" onClick={addHowItWorksCard}>
                  <FaPlus /> Add Step Card
                </button> */}
                {howItWorksCards.length < 3 && (
                  <button className="btn-add-row" onClick={addHowItWorksCard}>
                    <FaPlus /> Add Step Card
                  </button>
                )}
              </div>
            </>
          )}

          {/* ================= DISCOVER ASCENDER ================= */}

          {landingType === "discoverascender" && (
            <>
              <div className="form-group">
                <label>Section Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={discoverTitle}
                  onChange={(e) => {
                    setDiscoverTitle(e.target.value);
                  }}
                  placeholder="e.g. Discover Your Journey"
                />
              </div>

              <UploadBox
                accept="image/*"
                onFile={(file) => {
                  setDiscoverFiles((prev) => ({ ...prev, backgroundImage: file }));
                  if (file) {
                    const previewUrl = URL.createObjectURL(file);
                    setDiscoverPreviews((prev) => ({ ...prev, backgroundImage: previewUrl }));
                  }
                }}
                previewUrl={discoverPreviews.backgroundImage}
                fileName={discoverFiles.backgroundImage?.name}
              >
                <FaUpload className="upload-icon" />
                <p>Upload Background Image</p>
                <span>This appears as the large scenic image in the section</span>
              </UploadBox>

              <UploadBox
                accept="video/*"
                onFile={(file) => {
                  setDiscoverFiles((prev) => ({ ...prev, video: file }));
                  if (file) {
                    const previewUrl = URL.createObjectURL(file);
                    setDiscoverPreviews((prev) => ({ ...prev, video: previewUrl }));
                  }
                }}
                previewUrl={discoverPreviews.video}
                fileName={discoverFiles.video?.name}
              >
                <FaUpload className="upload-icon" />
                <p>Upload Video</p>
                <span>Upload a video for the section</span>
              </UploadBox>

              <div className="cms-subsection">
                <h3 className="subsection-title">Section Stats</h3>
                <p className="subsection-desc">
                  Enter the statistics that will be displayed
                </p>

                <div className="form-group">
                  <label>Total Distance (KM)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={discoverStats[0]?.label || ""}
                    onChange={(e) => {
                      const updated = [...discoverStats];
                      updated[0] = { ...updated[0], label: e.target.value };
                      setDiscoverStats(updated);
                    }}
                    placeholder="e.g. 1200 KM"
                  />
                </div>

                <div className="form-group">
                  <label>Active Users</label>
                  <input
                    type="text"
                    className="form-control"
                    value={discoverStats[0]?.value || ""}
                    onChange={(e) => {
                      const updated = [...discoverStats];
                      updated[0] = { ...updated[0], value: e.target.value };
                      setDiscoverStats(updated);
                    }}
                    placeholder="e.g. 5000+"
                  />
                </div>

                <div className="form-group">
                  <label>Customer Satisfaction (%)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={discoverStats[1]?.value || ""}
                    onChange={(e) => {
                      const updated = [...discoverStats];
                      updated[1] = { ...updated[1], value: e.target.value };
                      setDiscoverStats(updated);
                    }}
                    placeholder="e.g. 98%"
                  />
                </div>
              </div>
            </>
          )}

          {/* ================= OUR ATTRACTIONS ================= */}

          {landingType === "attractions" && (
            <>
              <div className="form-group">
                <label>Section Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={attractionsTitle}
                  onChange={(e) => {
                    setAttractionsTitle(e.target.value);
                  }}
                  placeholder="e.g. Our Attractions"
                />
              </div>

              <div className="form-group">
                <label>Section Subtitle</label>
                <textarea
                  className="form-control"
                  value={attractionsSubtitle}
                  onChange={(e) => {
                    setAttractionsSubtitle(e.target.value);
                  }}
                  placeholder="Enter subtitle"
                ></textarea>
              </div>

              <div className="cms-subsection">
                <h3 className="subsection-title">Attraction Items</h3>
                <p className="subsection-desc">
                  Each item appears as a masonry/grid tile with an image, title and tag.
                </p>

                {attractions.map((item, index) => (
                  <div className="cms-repeater-card" key={index}>
                    <div className="repeater-header">
                      <span className="repeater-label">Attraction {index + 1}</span>
                      {attractions.length > 1 && (
                        <button
                          className="btn-remove"
                          onClick={() => removeAttraction(index)}
                          title="Remove attraction"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>

                    <UploadBox
                      className="upload-box upload-box--sm"
                      accept="image/*"
                      onFile={(file) => setAttractionFile(index, file)}
                      previewUrl={attractionPreviews[index]}
                      fileName={attractionFiles[index]?.name}
                    >
                      <FaUpload className="upload-icon" />
                      <p>Upload Attraction Image</p>
                    </UploadBox>

                    <div className="cms-repeater-row">
                      <div className="form-group flex-1">
                        <label>Title</label>
                        <input
                          type="text"
                          className="form-control"
                          value={item.title}
                          onChange={(e) => updateAttraction(index, "title", e.target.value)}
                          placeholder="e.g. Mountain Climbing"
                        />
                      </div>
                      <div className="form-group flex-1">
                        <label>Tag / Category</label>
                        <input
                          type="text"
                          className="form-control"
                          value={item.tag}
                          onChange={(e) => updateAttraction(index, "tag", e.target.value)}
                          placeholder="e.g. Adventure"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button className="btn-add-row" onClick={addAttraction}>
                  <FaPlus /> Add Attraction
                </button>
              </div>
            </>
          )}

          {/* ================= START CHALLENGE BANNER ================= */}

          {landingType === "ctabanner" && (
            <>
              <UploadBox
                accept="image/*"
                onFile={(file) => {
                  setCtaFiles((prev) => ({ ...prev, mapImage: file }));
                  if (file) {
                    const previewUrl = URL.createObjectURL(file);
                    setCtaPreviews((prev) => ({ ...prev, mapImage: previewUrl }));
                  }
                }}
                previewUrl={ctaPreviews.mapImage}
                fileName={ctaFiles.mapImage?.name}
              >
                <FaUpload className="upload-icon" />
                <p>Upload Banner Background Image</p>
                <span>This is the dark scenic background for the bottom CTA strip</span>
              </UploadBox>

              <UploadBox
                accept="image/*"
                onFile={(file) => {
                  setCtaFiles((prev) => ({ ...prev, topAvatarImage: file }));
                  if (file) {
                    const previewUrl = URL.createObjectURL(file);
                    setCtaPreviews((prev) => ({ ...prev, topAvatarImage: previewUrl }));
                  }
                }}
                previewUrl={ctaPreviews.topAvatarImage}
                fileName={ctaFiles.topAvatarImage?.name}
              >
                <FaUpload className="upload-icon" />
                <p>Upload Top Avatar Image</p>
              </UploadBox>

              <UploadBox
                accept="image/*"
                onFile={(file) => {
                  setCtaFiles((prev) => ({ ...prev, bottomImage1: file }));
                  if (file) {
                    const previewUrl = URL.createObjectURL(file);
                    setCtaPreviews((prev) => ({ ...prev, bottomImage1: previewUrl }));
                  }
                }}
                previewUrl={ctaPreviews.bottomImage1}
                fileName={ctaFiles.bottomImage1?.name}
              >
                <FaUpload className="upload-icon" />
                <p>Upload Bottom Image 1</p>
              </UploadBox>

              <UploadBox
                accept="image/*"
                onFile={(file) => {
                  setCtaFiles((prev) => ({ ...prev, bottomImage2: file }));
                  if (file) {
                    const previewUrl = URL.createObjectURL(file);
                    setCtaPreviews((prev) => ({ ...prev, bottomImage2: previewUrl }));
                  }
                }}
                previewUrl={ctaPreviews.bottomImage2}
                fileName={ctaFiles.bottomImage2?.name}
              >
                <FaUpload className="upload-icon" />
                <p>Upload Bottom Image 2</p>
              </UploadBox>

              <UploadBox
                accept="image/*"
                onFile={(file) => {
                  setCtaFiles((prev) => ({ ...prev, bottomImage3: file }));
                  if (file) {
                    const previewUrl = URL.createObjectURL(file);
                    setCtaPreviews((prev) => ({ ...prev, bottomImage3: previewUrl }));
                  }
                }}
                previewUrl={ctaPreviews.bottomImage3}
                fileName={ctaFiles.bottomImage3?.name}
              >
                <FaUpload className="upload-icon" />
                <p>Upload Bottom Image 3</p>
              </UploadBox>

              <div className="form-group">
                <label>Banner Heading</label>
                <input
                  type="text"
                  className="form-control"
                  value={ctaBannerHeading}
                  onChange={(e) => {
                    setCtaBannerHeading(e.target.value);
                  }}
                  placeholder="e.g. Start Challenge and Unlock Rewards!"
                />
              </div>

              <div className="form-group">
                <label>Banner Subtext</label>
                <textarea
                  className="form-control"
                  value={ctaBannerSubtext}
                  onChange={(e) => {
                    setCtaBannerSubtext(e.target.value);
                  }}
                  placeholder="Enter supporting text"
                ></textarea>
              </div>

              <div className="form-group">
                <label>Button Text</label>
                <input
                  type="text"
                  className="form-control"
                  value={ctaBannerBtnText}
                  onChange={(e) => {
                    setCtaBannerBtnText(e.target.value);
                  }}
                  placeholder="e.g. Get Started Now"
                />
              </div>
            </>
          )}

          {/* ================= SAVE BUTTON ================= */}
          <div className="d-flex gap-3 mt-3">
            <button
              type="button"
              className="marketing__create-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      )}

      {activeTab === "admin" && <AdminRoles />}

      {activeTab === "audit" && <AuditLogs />}
    </div>
  );
};

export default PlatformCMS;